import { useCallback, useContext, useMemo, useState } from 'react';
import UsersContext from 'contexts/UsersContext';
import STORE from 'store';
import AccountPickerContext from 'contexts/AccountPickerContext';
import isDefined from 'isDefined';
import useLockedDebouncedEffect from 'hooks/useLockedDebouncedEffect';

interface GetUnMappedUsersArgs {}

const useUsersNotMappedToEntityId = (entityId: string) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { signedUpAndPendingUsers, isLoading: isLoadingUsers } =
    useContext(UsersContext);
  const [unMappedUsers, setUnMappedUsers] = useState<
    (UserManagement.PendingUser | UserManagement.SignedUpUser)[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getUnMappedUsers = useCallback(
    async (
      _args: GetUnMappedUsersArgs,
    ): Promise<
      (UserManagement.PendingUser | UserManagement.SignedUpUser)[]
    > => {
      setIsLoading(true);

      const promises = signedUpAndPendingUsers.map(
        async (
          user,
        ): Promise<
          undefined | UserManagement.SignedUpUser | UserManagement.PendingUser
        > => {
          const userContentSettings = await STORE.userManagement
            .getUserContentSettingsRef({
              accountId: selectedAccountId,
              userId: user.id,
            })
            .get();

          if (!userContentSettings.exists) {
            return user;
          }
          const settings = userContentSettings.data();
          if (!settings) {
            return user;
          }
          if (settings.mode === 'normal') {
            return user;
          }

          const isThisUsedMappedToThisEntity = settings.mappings.some(
            (mapping) => mapping.entityId === entityId,
          );

          if (!isThisUsedMappedToThisEntity) {
            return user;
          }

          return undefined;
        },
      );

      const results = await Promise.all(promises);
      return results.filter(isDefined);
    },
    [entityId, signedUpAndPendingUsers, selectedAccountId],
  );

  const args = useMemo(() => {
    if (isLoadingUsers) {
      return undefined;
    }

    return {};
  }, [isLoadingUsers]);

  const handleResponse = useCallback(
    (
      response: (UserManagement.PendingUser | UserManagement.SignedUpUser)[],
    ) => {
      setUnMappedUsers(response);
      setIsLoading(false);
    },
    [],
  );

  useLockedDebouncedEffect({
    args,
    responseHandler: handleResponse,
    callback: getUnMappedUsers,
  });

  return { unMappedUsers, isLoading };
};

export default useUsersNotMappedToEntityId;
